import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, DatePicker, Select, Space, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import BaseForm from '../../../../components/form/BaseForm';
import SearchBox from '../../../../components/outline/SearchBox';
import Button from '../../../../components/Button';
import {
  BatchStatus,
  BatchSummary,
  ListBatchesQuery,
} from '../../../../lib/api/settlementDateAdjustment';
import { settlementDateAdjustmentQuery } from '../../queries';
import {
  SETTLEMENT_DATE_ADJUSTMENT_BATCH_STATUS_LABEL,
  SETTLEMENT_DATE_ADJUSTMENT_SOURCE_LABEL,
} from '../../constants/labels';
import { formatDate } from '../../../../lib/utils/date/formatDate';
import { DATE_FORMAT } from '../../../../types/common/date';
import { BatchDetailModal } from '../BatchDetailModal/BatchDetailModal';
import { StyledContainer } from './styles';

const STATUS_COLOR: Record<BatchStatus, string> = {
  pending: 'default',
  executed: 'blue',
  partiallyRolledBack: 'orange',
  fullyRolledBack: 'default',
  failed: 'red',
};

const INITIAL_FILTER: ListBatchesQuery = { page: 1, pageSize: 20 };

/**
 * 탭2: 변경 이력 / 롤백.
 *
 * 필터는 검색/초기화 버튼 누를 때만 적용(ExecuteTabPanel과 동일한 SearchBox 컨벤션).
 * 배치 목록 → 행 클릭 시 상세 모달(배치 item 리스트 + 롤백 + 검증).
 */
export const HistoryTabPanel = (): React.JSX.Element => {
  // draft(편집 중) vs applied(실제 쿼리) 분리 — 검색 버튼 눌러야 반영
  const [draftFilter, setDraftFilter] = useState<ListBatchesQuery>(INITIAL_FILTER);
  const [appliedFilter, setAppliedFilter] = useState<ListBatchesQuery>(INITIAL_FILTER);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);

  const batchesQuery = useQuery(settlementDateAdjustmentQuery.batches(appliedFilter));
  const batches: BatchSummary[] = batchesQuery.data?.batches ?? [];

  const handleSearch = (): void => {
    setAppliedFilter({ ...draftFilter, page: 1 });
  };

  const handleResetFilter = (): void => {
    setDraftFilter(INITIAL_FILTER);
    setAppliedFilter(INITIAL_FILTER);
  };

  const columns = [
    { title: '배치 ID', dataIndex: 'batchId', width: 220, ellipsis: true as const },
    { title: '관리자', dataIndex: 'adminName', width: 100 },
    {
      title: '선정 방식',
      dataIndex: 'source',
      width: 120,
      render: (v?: BatchSummary['source']) =>
        v ? (
          <Tag color={v === 'excelUpload' ? 'geekblue' : 'default'}>{SETTLEMENT_DATE_ADJUSTMENT_SOURCE_LABEL[v]}</Tag>
        ) : (
          '-'
        ),
    },
    { title: '변경 사유', dataIndex: 'reason', ellipsis: true as const },
    {
      title: '이동 내용',
      key: 'shift',
      width: 130,
      render: (_: unknown, r: BatchSummary) =>
        r.shiftMode === 'delta' ? `+${r.deltaDays ?? 0}일` : `→ ${r.targetBaseDate}`,
    },
    { title: '대상 건수', dataIndex: 'totalCount', width: 90 },
    { title: '적용 건수', dataIndex: 'appliedCount', width: 90 },
    { title: '롤백 건수', dataIndex: 'rolledBackCount', width: 90 },
    {
      title: '배치 상태',
      key: 'status',
      width: 110,
      render: (_: unknown, r: BatchSummary) => (
        <Tag color={STATUS_COLOR[r.status]}>{SETTLEMENT_DATE_ADJUSTMENT_BATCH_STATUS_LABEL[r.status]}</Tag>
      ),
    },
    {
      title: '실행 일시',
      dataIndex: 'executedAt',
      width: 170,
      render: (v?: string) => formatDate(v, DATE_FORMAT.FULL_DASH_FORMAT),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: unknown, r: BatchSummary) => (
        <Button text="상세" color="light" onClick={() => setSelectedBatchId(r.batchId)} />
      ),
    },
  ];

  return (
    <StyledContainer>
      <Alert type="info" showIcon message="변경 이력. 행 상세에서 부분/전체 롤백, 검증(verify)을 수행할 수 있습니다." />

      <SearchBox onSearch={handleSearch} onClear={handleResetFilter}>
        <BaseForm
          label="실행 기간"
          content={
            <DatePicker.RangePicker
              value={draftFilter.from && draftFilter.to ? [dayjs(draftFilter.from), dayjs(draftFilter.to)] : null}
              onChange={(dates) =>
                setDraftFilter({
                  ...draftFilter,
                  // RangePicker는 선택 날짜의 00:00(로컬)을 반환하므로
                  // `from`은 startOf('day'), `to`는 endOf('day')로 보정해
                  // 사용자가 고른 날짜 전체(00:00~23:59.999 KST)가 쿼리에 포함되도록 한다.
                  // toISOString()은 자동으로 UTC로 변환되어 서버에 전달 → 백엔드(KST 서버)에서 올바르게 매핑.
                  from: dates?.[0]?.startOf('day').toISOString(),
                  to: dates?.[1]?.endOf('day').toISOString(),
                })
              }
            />
          }
        />
        <BaseForm
          label="배치 상태"
          content={
            <Select
              allowClear
              style={{ width: 200 }}
              value={draftFilter.status}
              onChange={(v) => setDraftFilter({ ...draftFilter, status: v })}
              options={(Object.keys(SETTLEMENT_DATE_ADJUSTMENT_BATCH_STATUS_LABEL) as BatchStatus[]).map((k) => ({
                value: k,
                label: SETTLEMENT_DATE_ADJUSTMENT_BATCH_STATUS_LABEL[k],
              }))}
            />
          }
        />
      </SearchBox>

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Table<BatchSummary>
          rowKey="batchId"
          size="small"
          loading={batchesQuery.isLoading}
          dataSource={batches}
          columns={columns}
          pagination={{
            current: appliedFilter.page ?? 1,
            pageSize: appliedFilter.pageSize ?? 20,
            total: batchesQuery.data?.totalCount ?? 0,
            showSizeChanger: true,
            onChange: (page, pageSize) => setAppliedFilter({ ...appliedFilter, page, pageSize }),
          }}
          scroll={{ x: 'max-content' }}
        />
      </Space>

      <BatchDetailModal batchId={selectedBatchId} onClose={() => setSelectedBatchId(null)} />
    </StyledContainer>
  );
};
